/**
 * @file FormSection.jsx
 * @author Rishikesh
 * @date 2024-09-09
 * @description Component of leads to manage form
 */
import React from "react";
import {
  Divider,
  Stack,
  TextField,
  Typography,
  Skeleton,
  Box,
  MenuItem,
  Switch,
} from "@mui/material";
import {
  emailRegex,
  phoneNumberRegex,
} from "../../../components/constants/regex";
import { DatePicker } from "@mui/x-date-pickers";
import { Controller } from "react-hook-form";
import { useSelector } from "react-redux";

export const FormSection = ({
  isLoading,
  register,
  errors,
  control,
  watch,
  setValue,
  userList,
  agentList,
  today,
}) => {
  const role = useSelector((state) => state.auth.user?.role);
  const isActive = watch("isactive");
  const validatePersonalPhone = (value) => {
    // Allow "XXXXXX" as a valid value
    if (value.slice(0, 6) === "XXXXXX") {
      return true;
    }

    // Check if the value matches the phone number regex
    const isValid = phoneNumberRegex.test(value);

    return isValid || "Enter a valid phone number"; // Return error message if invalid
  };

  const handleSwitchChange = (event) => {
    setValue("isactive", event.target.checked ? 1 : 0);
  };

  return (
    <Box
      width={{ xs: "100%", md: 360 }}
      height="100%"
      borderRadius="12px"
      px="8px"
      py={3}
      bgcolor="#F7F7F7"
      display="flex"
      flexDirection="column"
    >
      <Stack
        pb={1}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        {isLoading ? (
          <Skeleton variant="rounded" width={140} height={24} />
        ) : (
          <Typography fontWeight={500} fontSize={20} color="#222222">
            Enter details
          </Typography>
        )}
      </Stack>
      <Divider />
      <Box overflow="auto" pt={2}>
        {/* Name Field */}
        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 3, mb: 1 }}
            width={"100%"}
            height={40}
          />
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={"space-between"}
          >
            <Typography>Name</Typography>
            <TextField
              placeholder="Enter full name"
              className="inputMdRounded"
              sx={{ bgcolor: "transparent" }}
              error={!!errors.name}
              helperText={errors.name ? errors.name.message : ""}
              {...register("name", { required: "Name is required" })}
            />
          </Stack>
        )}

        {/* Email Field */}
        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 3, mb: 1 }}
            width={"100%"}
            height={40}
          />
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={"space-between"}
          >
            <Typography>Email</Typography>
            <TextField
              placeholder="Enter email address"
              className="inputMdRounded"
              sx={{ bgcolor: "transparent" }}
              error={!!errors.email}
              helperText={errors.email ? errors.email.message : ""}
              {...register("email", {
                pattern: {
                  value: emailRegex,
                  message: "Enter a valid email",
                },
              })}
            />
          </Stack>
        )}

        {/* Personal No Field */}
        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 3, mb: 1 }}
            width={"100%"}
            height={40}
          />
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={"space-between"}
          >
            <Typography>Personal No.</Typography>
            <TextField
              placeholder="Enter personal number"
              className="inputMdRounded"
              sx={{ bgcolor: "transparent" }}
              error={!!errors.personalphone}
              slotProps={{ htmlInput: { maxLength: 10 } }}
              helperText={
                errors.personalphone ? errors.personalphone.message : ""
              }
              {...register("personalphone", {
                required: "Personal No. is required",
                validate: validatePersonalPhone,
              })}
            />
          </Stack>
        )}

        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 3, mb: 1 }}
            width={"100%"}
            height={40}
          />
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={"space-between"}
          >
            <Typography>Date of enquiry</Typography>
            <Controller
              name="enquirydate"
              control={control}
              rules={{ required: "Date of enquiry is required" }}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  format="LL"
                  className="inputMdRounded"
                  sx={{ bgcolor: "transparent", width: 206 }}
                  // If no date is selected, restrict selection to today only
                  minDate={field.value || today}
                  maxDate={field.value || today}
                  error={!!errors.enquirydate}
                  helperText={
                    errors.enquirydate ? errors.enquirydate.message : ""
                  }
                  onAccept={(date) => {
                    // Ensure only the chosen date or today can be selected
                    if (
                      !date ||
                      date.isSame(today, "day") ||
                      date.isSame(field.value, "day")
                    ) {
                      field.onChange(date);
                    } else {
                      field.onChange(null); // Clear if an invalid date is selected
                    }
                  }}
                />
              )}
            />
          </Stack>
        )}

        {role !== "employee" &&
          role !== "agent" &&
          (isLoading ? (
            <Skeleton
              variant="rounded"
              sx={{ mt: 3, mb: 1 }}
              width={"100%"}
              height={40}
            />
          ) : (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent={"space-between"}
            >
              <Typography>Assign to</Typography>
              <TextField
                sx={{
                  "& .MuiInputBase-input": {
                    m: 0,
                    p: "4px 8px",
                    width: 166,
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    height: 44,
                  },
                  my: 1,
                  width: "auto",
                }}
                defaultValue=""
                value={watch("ownerid")}
                onChange={(e) => setValue("ownerid", e.target.value)}
                select
                variant="outlined"
                placeholder="Select"
                size="small"
                error={!!errors.ownerid}
                helperText={errors.ownerid ? errors.ownerid.message : ""}
                {...register("ownerid", {
                  required: "Choose an employee",
                })}
              >
                {userList.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          ))}
        {role !== "employee" &&
          role !== "agent" &&
          (isLoading ? (
            <Skeleton
              variant="rounded"
              sx={{ mt: 3, mb: 1 }}
              width={"100%"}
              height={40}
            />
          ) : (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent={"space-between"}
            >
              <Typography>Agent</Typography>
              <TextField
                sx={{
                  "& .MuiInputBase-input": {
                    m: 0,
                    p: "4px 8px",
                    width: 166,
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    height: 44,
                  },
                  my: 1,
                  width: "auto",
                }}
                defaultValue=""
                value={watch("agentid")}
                onChange={(e) => setValue("agentid", e.target.value)}
                select
                variant="outlined"
                placeholder="Select"
                size="small"
                error={!!errors.agentid}
                helperText={errors.agentid ? errors.agentid.message : ""}
                // {...register("agentid", {
                //   required: "Choose an agent",
                // })}
              >
                {agentList.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          ))}
        {/* isActive Field */}
        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 3, mb: 1 }}
            width={"100%"}
            height={40}
          />
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={"space-between"}
          >
            <Typography>Is Active ?</Typography>

            <Switch
              checked={isActive === 1}
              onChange={handleSwitchChange}
              inputProps={{ "aria-label": "controlled" }}
            />
          </Stack>
        )}
      </Box>
    </Box>
  );
};
